/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * VisRenderSupport
 * INTENT: VisQuerySupport - implements the portion of the VisAPI that is focused on properties and the property manager.
 */

define([
	'jquery',
	'underscore',
	'../../query/VisQueryExecution',
	'../../query/VisFPDExecution',
	'../../query/smarts/VisKeyDriverExecution',
	'../../../../lib/@waca/core-client/js/core-client/ui/core/Events',
	'../../query/QueryService',
	'../../query/CommonQueryHelper',
	'../../query/QueryResultData',
	'../../../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'./VisPossibleKeyDriversManager'
], function(
	$,
	_,
	VisQueryExecution,
	VisFPDExecution,
	VisKeyDriverExecution,
	Events,
	QueryService,
	CommonQueryHelper,
	QueryResultData,
	BrowserUtils,
	PossibleKeyDriversManager
) {

	'use strict';

	let VisQuerySupport = Events.extend({

		init: function(options) {
			//Initialize a minimal set of attributes (nested collections from spec etc)
			VisQuerySupport.inherited('init', this, arguments);

			options = options || {};
			this.visModelManager = options.visModelManager;
			this.ownerWidget = options.ownerWidget;
			this.propertySupport = options.propertySupport;
			this.pageContext = options.pageContext;
			this.logger = options.logger;
			this.content = options.ownerWidget.content;
			this.visualization = this.content.getFeature('Visualization');

			this.queryService = new QueryService({
				dashboardApi: this.ownerWidget.getDashboardApi()
			});

			this.queryHelper = new CommonQueryHelper({
				'queryService': this.queryService
			}, this.logger);

			//Normally, this array is empty, however; a caller can insert a handler that is called
			//with the query results of a dss query PRIOR to postProcessing.
			this._unprocessedResultDataHandlers = [];

			this.api = {
				getQueryHelper: () => this.queryHelper,
				createQueryResultData: this.createQueryResultData.bind(this),
				getQueryDefinition: this.getQueryDefinition.bind(this),
				runSmartsQuery: this._runSmartsQuery.bind(this),

				//Any caller that needs to work with the unprocessed result data can register this handler.
				//The handlers will be called synchronously prior to any post-processing.
				addUnprocessedResultDataHandler: this._addUnprocessedResultDataHandler.bind(this),
				removeUnprocessedResultDataHandler: this._removeUnprocessedResultDataHandler.bind(this)
			};
		},

		initialize: function(visAPI) {
			this.visQueryExecution = new VisQueryExecution({
				'queryService': this.queryService,
				'ownerWidget': this.ownerWidget,
				'metadataAPI': this.visModelManager.getMetadataAPI(),
				'pageContext': this.pageContext,
				'visAPI': visAPI,
				queryHelper: this.queryHelper,
				//pass an api which can be used to call registered handlers with the un-preprocessed query result data.
				callUnprocessedResultDataHandlers: this._callUnprocessedResultDataHandlers.bind(this)
			});

			// get smart services
			this.smartsService = this.ownerWidget.getDashboardApi().getFeature('SmartsService');
			const keyDriverService = this.ownerWidget.getDashboardApi().getFeature('KeyDriverService');

			this.visKeyDriverExecution = new VisKeyDriverExecution({
				keyDriverService: keyDriverService,
				logger: this.logger
			});

			this.visPossibleKeyDriversManager = new PossibleKeyDriversManager({
				visAPI: visAPI,
				visAnnotationExecution: this.visAnnotationExecution,
				logger: this.logger,
				ownerWidget: this.ownerWidget,
				smartsEnabled: this.ownerWidget.contributionSpec.enableSmarts // TODO: Remove
			});

			this.visFPDExecution = new VisFPDExecution({
				keyDriverManager: this.visPossibleKeyDriversManager,
				ownerWidget: this.ownerWidget,
				metadataAPI: this.visModelManager.getMetadataAPI(),
				queryHelper: this.queryHelper
			});

			this.smartAnnotationsFeature = this.content && this.content.getFeature('SmartAnnotations.deprecated');
			this.forecastFeature = this.content && this.content.getFeature('Forecast');
		},

		getAPI: function() {
			return this.api;
		},

		_runSmartsQuery: function(options) {
			return this.smartsService.runQuery(options);
		},

		/**
		 * create a queryResultData instance to process the supplied queryResult.
		 * @param queryResult - the queryResult from a DSS query.
		 * @returns a queryResultData instance.
		 */
		createQueryResultData: function(queryResult) {
			return new QueryResultData(queryResult._resultData);
		},

		getDefinition: function() {
			return this.visModelManager.getDefinition();
		},
		getDashboardProperties: function() {
			return this.ownerWidget.boardModel.properties;
		},
		getLocalFilters: function() {
			return this.visModelManager.localFilters;
		},
		getSearchFilters: function() {
			return this.visModelManager.searchFilters;
		},
		getQueryExecution: function() {
			return this.visQueryExecution;
		},
		/**
		* If there are UnprocessedResultDataHandlers registered for thi FPD request call,
		* we will execute this handler with the passed in options and data.
		* options { 'type' : 'FPD' } indicates this is FPD call
		*/
		executeFastPatternDetectionRequest: function(targetId, possibleKeyDrivers, methods, promptControlFunctions = null){
			//There are cases that we set prefected render data to widget,
			//we want to reuse the prefethced data
			return this.visFPDExecution.execute({ targetId, possibleKeyDrivers, methods, promptControlFunctions })
				.then((data)=>{
					if(this.visualization.getDefinition().getProperty('useFPDAsInsightsInput')){
						this.ownerWidget.trigger('widgetFPDDataReady', { data: data, visId: this.visModelManager.getDefinition().id });
						this._callUnprocessedResultDataHandlers(data, { 'type' : 'FPD' });
					}
					return Promise.resolve(data);
				});
		},
		getPossibleKeyDriversErrorState: function() {
			return this.visPossibleKeyDriversManager.getPossibleKeyDriversErrorState();
		},
		whenPossibleKeyDriversAreReady: function(renderContext, targetId, useModelData = true) {
			return this.visPossibleKeyDriversManager.whenPossibleKeyDriversAreReady(renderContext, targetId, useModelData);
		},
		getPossibleKeyDriversManger: function() {
			return this.visPossibleKeyDriversManager;
		},
		getEnabledPossibleKeyDrivers: function(targetId) {
			return this.visPossibleKeyDriversManager.getEnabledPossibleKeyDrivers(targetId);
		},
		getNewPossibleKeyDriversForTargetId: function(targetId) {
			return this.visPossibleKeyDriversManager.getNewPossibleKeyDriversForTargetId(targetId);
		},
		getPossibleKeyDriversDisplayState: function(itemId) {
			return this.visPossibleKeyDriversManager.getPossibleKeyDriversDisplayState(itemId);
		},
		getKeyDrivers: function(dataItem) {
			return this.visPossibleKeyDriversManager.getKeyDrivers(dataItem);
		},
		possibleKeyDriverSelectionStateChanged(targetId, newlySelectedKeyDriverDisplays) {
			this.visPossibleKeyDriversManager.possibleKeyDriverSelectionStateChanged(targetId, newlySelectedKeyDriverDisplays);
		},
		/**
		* @param {String} targetId of the target data item.
		* @param {String} assertId of the module
		* @returns {Promise} a promise which is resolved when the possible key drivers
		* are ready. This results in a set of possible key drivers.
		*/
		whenPossibleKeyDriversQueryIsReady: function(targetId, assetId) {
			const module = this.visModelManager.getModule();
			const payload = {
				targetId: targetId,
				assetId: assetId,
				tempModule: module.getTemporarySessionModuleJSON(),
				hasShaping: (!!module.getShapingId()) //module has shaping
			};
			return module.saveTemporaryModule().then((shapingModuleId) => {
				if (assetId !== shapingModuleId) {
					payload.tempModuleId = shapingModuleId;
				}
				return this.visKeyDriverExecution.execute(payload);
			});
		},

		areThereValidInsights: function() {
			return this.smartAnnotationsFeature ? this.smartAnnotationsFeature.areThereValidInsights() : false;
		},
		areThereValidFPDMessages() {
			return this.smartAnnotationsFeature ? this.smartAnnotationsFeature.areThereValidFPDMessages() : false;
		},

		getSmartAnnotationsManger: function() {
			return this.smartAnnotationsFeature;
		},
		getEnabledAnnotations: function() {
			return this.smartAnnotationsFeature ? this.smartAnnotationsFeature.getEnabledAnnotations() : [];
		},
		getAllSuggestedAnnotations: function() {
			return this.smartAnnotationsFeature ? this.smartAnnotationsFeature.getAllSuggestedAnnotations() : [];
		},
		resetAnnotations: function() {
			this.smartAnnotationsFeature && this.smartAnnotationsFeature.resetAnnotations();
		},
		setAnnotationMessages: function(messages) {
			this.smartAnnotationsFeature && this.smartAnnotationsFeature.setAnnotationMessages(messages);
		},
		clearInsightsIndicatorMessages() {
			this.smartAnnotationsFeature && this.smartAnnotationsFeature.clearInsightsIndicatorMessages();
		},

		_doesPredictQueryInvolvesOlap() {
			const visualization = this.content.getFeature('Visualization');
			return visualization.getDataSource().isOlapPackage() || this.visQueryExecution.querySpecInvolvesOlapColumn();
		},

		whenPredictIsReady(renderContext, viewSupportsAnnotations, viewSupportsForecasts) {
			if (viewSupportsAnnotations || viewSupportsForecasts) {
				let tags = [];
				if (this._doesPredictQueryInvolvesOlap() ) { tags.push('olap'); }
				if (viewSupportsAnnotations) { tags.push('annotatable'); }
				if (viewSupportsForecasts) { tags.push('forecasting'); }
				const smartsExec = this.content.getFeature('SmartsExecution');
				return smartsExec.execute({
					renderContext: renderContext,
					target: smartsExec.TARGETS.SUGGESTIONS,
					sender: 'whenPredictIsReady',
					tags: tags
				});
			} else {
				return Promise.resolve({});
			}
		},

		getQueryManager: function() {
			return this.getQueryExecution().getQueryManager();
		},
		/**
		 * @returns the query results object which owns field values from the last executed query
		 */
		getQueryResults: function() {
			return this.getQueryManager().getQueryResults();
		},

		/**
		 * @returns the query definition object which defines fields for the query.
		 */
		getQueryDefinition: function() {
			return this.getQueryManager().getQueryDefinition();
		},

		whenColumnsMinMaxQueryReady: function(sourceIdOrModule, queryDefinition, queryContext) {
			return this.queryHelper.whenColumnsMinMaxQueryReady(sourceIdOrModule, queryDefinition, queryContext);
		},

		whenDataItemsMinMaxQueryReady: function(sourceIdOrModule, queryDefinition, queryContext) {
			return this.queryHelper.whenDataItemsMinMaxQueryReady(sourceIdOrModule, queryDefinition, queryContext);
		},

		remove: function() {
			// cleanup external events
			for (let key in this.ownerWidgetEvents) {
				this.ownerWidget.off(key, this.ownerWidgetEvents[key], this);
			}
		},

		_notifyViews: function(eventName, payload) {
			this.visModelManager.trigger(eventName, payload);
		},

		/**
		 * @param extraQueryOptions	(optional) extra options to set query, including needsUnaggregatedForm, extraDataItems and extraProjections
		 * @returns the options to be sent on the query based on property and definition values
		 */
		_getQueryOptions: function(extraQueryOptions) {
			let queryOptions = {
				shouldAxisScaleBeFixed: this.propertySupport.getPropertyValue('maintainAxisScales')
			};

			let definition = this.getDefinition();

			if (definition && definition.dataRowLimit) {
				//Some charts have difficulty rendering a large set of rows, in this case, a smaller dataRowLimit is set in the definition.
				queryOptions.dataRowLimit = definition.dataRowLimit;

				// Reduce data limit on IE
				if (BrowserUtils.isIE() && definition.dataRowLimitIE) {
					queryOptions.dataRowLimit = definition.dataRowLimitIE;
				}
			}

			if (definition.useMapbox) {
				queryOptions.useMapbox = true;
			}
			if (extraQueryOptions) {
				if (typeof extraQueryOptions.needsUnaggregatedForm !== 'undefined') {
					queryOptions.needsUnaggregatedForm = extraQueryOptions.needsUnaggregatedForm;
				}
				if (extraQueryOptions.extraDataItems) {
					queryOptions.extraDataItems = extraQueryOptions.extraDataItems;
				}
				if (extraQueryOptions.filterDataItems) {
					queryOptions.filterDataItems = extraQueryOptions.filterDataItems;
				}
				if (extraQueryOptions.extraProjections) {
					queryOptions.extraProjections = extraQueryOptions.extraProjections;
				}
				if (extraQueryOptions.filterProjections) {
					queryOptions.filterProjections = extraQueryOptions.filterProjections;
				}
				if (extraQueryOptions.extraFilters) {
					queryOptions.extraFilters = extraQueryOptions.extraFilters;
				}
				if (extraQueryOptions.excludeAnnotationCalcs) {
					queryOptions.excludeAnnotationCalcs = extraQueryOptions.excludeAnnotationCalcs;
				}
			}
			return queryOptions;
		},

		_getEntities: function() {
			return {
				definition: this.getDefinition(),
				localFilters: this.getLocalFilters(),
				searchFilters: this.getSearchFilters(),
				properties: this.getDashboardProperties(),
				preferredModelItems: this._getRelatedModelItems()
			};
		},

		/**
		 * Ensure that the slots are up-to-date and prepare the data (if necessary).
		 * @param {object} renderContext
		 * @param {boolean=} localFiltersOnly (optional) if set, only local filters will be passed to the queryDefinition. If not set, both local and cross-widget filters are passed.
		 * @param {object=} extraQueryOptions (optional) - extra query option
		 * @returns a promise.
		 */
		whenQueryResultsReady: function(renderContext, localFiltersOnly, extraQueryOptions) {
			let executionOptions = this.getExecutionOptions(renderContext, localFiltersOnly, extraQueryOptions);
			return this.getQueryExecution().execute(executionOptions);
		},

		/**
		 * Add a handler that is called with the unprocessed DSS query result (ie: un-post-processed)
		 * @param cbHandler a callback function that can use the unprocessed (aka: un-post-processed) DSS result.
		 * @returns the handler added to the collection.
		 */
		_addUnprocessedResultDataHandler: function(cbHandler) {
			this._unprocessedResultDataHandlers.push(cbHandler);
			return cbHandler;
		},

		/**
		 * Remove a handler that was added with addUnprocessedResultDataHandler.
		 * @param cbHandler - a handler callback to be removed.
		 * @returns true if any handler was removed.
		 */
		_removeUnprocessedResultDataHandler: function(cbHandler) {
			const initialLength = this._unprocessedResultDataHandlers.length;
			this._unprocessedResultDataHandlers = this._unprocessedResultDataHandlers.filter( (handler) => (handler !== cbHandler) );
			return initialLength > this._unprocessedResultDataHandlers.length;
		},

		/**
		 * call any handlers added via addUnprocessedResultDataHandler with the unprocessed (aka: un-post-processed) DSS resultdata.
		 * NOTE: These handlers are called synchronously to ensure integrity of the results data object.
		 * @param unprocessedResultData
		 * @param options  such as {type: 'FPD'}, to distingush the type of the nprocessedResultData
		 */
		_callUnprocessedResultDataHandlers: function(unprocessedResultData, options={ type:'DSS' }) {
			this._unprocessedResultDataHandlers.forEach((handler) => handler(unprocessedResultData, options));
		},

		/**
		 * Get the execution options for vis query execution
		 * @param {object} renderContext
		 * @param {boolean} localFiltersOnly (optional) - if set, only local filters will be passed to the queryDefinition. If not set, both local and cross-widget filters are passed.
		 * @param {object} extraQueryOptions (optional) - the extra options to build query
		 * @param {object} extraQueryOptions.needsUnaggregatedForm (optional) - if set to true the query will have 'detail' type and ignore aggregation
		 * @param {object[]} extraQueryOptions.extraDataItems (optional) - an array of extra dataItems to be included in the query
		 * @param {function(object):boolean} filterDataItems (optional) - a callback function to filter the dataItems list
		 * @param {object[]} extraQueryOptions.extraProjections (optional) - an array of extra projections to be included in the query
		 * @param {function(object):boolean} filterProjections (optional) - a callback function to filter the projection list
		 * @param {object[]} extraQueryOptions.extraFilters (optional) - an array of extra filters to be included in the query
		 * @return {object} Execition options object
		 */
		getExecutionOptions: function(renderContext, localFiltersOnly, extraQueryOptions) {
			return {
				renderContext: renderContext,
				invalidReason: this._invalidReason,	//Previously set as invalid....so will just throw _error.
				localFiltersOnly: localFiltersOnly,
				entities: this._getEntities(),
				queryOptions: this._getQueryOptions(extraQueryOptions),
				sender: extraQueryOptions ? extraQueryOptions.sender : undefined,
				auxQuery: !_.isEmpty(extraQueryOptions), // auxiliary query generated with extra options
				scope: this.ownerWidget.getContainerPageId(),
				archetype: this.visModelManager.archetype
			};
		},

		/**
		* @param  {object} renderContext
		* @returns {Promise} a promise which is resolved when the annotation executions
		* service completes. This results in an annotated set of data.
		*/
		whenAnnotatedResultsReady: function(renderContext) {
			const module = this.visModelManager.getModule();
			const assetId = module.getAssetId();
			const smartsExec = this.content.getFeature('SmartsExecution');
			const payload = {
				renderContext: renderContext,
				target: smartsExec.TARGETS.EXECUTIONS,
				sender: 'whenAnnotatedResultsReady',
				moduleId: assetId,
				tempModule: module.getTemporarySessionModuleJSON(),
				shapedModule: (!!module.getShapingId()) //module has shaping
			};
			return module.saveTemporaryModule().then((shapingModuleId) => {
				if (assetId !== shapingModuleId) {
					payload.tempModuleId = shapingModuleId;
				}
				return smartsExec.execute(payload);
			});
		},

		queryChanged: function(options) {
			options = options || {};
			return this.getQueryExecution().queryChanged({ sourceIds: options.sourceIds, assetIds: options.assetIds, entities: this._getEntities() });
		},

		_getRelatedModelItems: function() {
			let boardModel = this.ownerWidget.boardModel;
			let sourceId = this.ownerWidget.content.getFeature('Visualization').getDataSource().getId();
			let eventGroup = this.ownerWidget.eventGroups.findGroup(this.ownerWidget.id);
			let aModelItems = [];

			// Collect query subject from widgets
			for (let key in boardModel.widgetInstances) {
				// Skip self
				if (key === this.ownerWidget.id) {
					continue;
				}

				let widget = boardModel.widgetInstances[key];
				if (eventGroup && eventGroup.widgetIds.indexOf(widget.id) === -1) {
					// Not in the same event group
					continue;
				}

				// Collect metadata from dataViews, i.e. projected data items
				let dataViews = widget.data && widget.data.dataViews;
				if (!dataViews) {
					continue;
				}

				dataViews.forEach(dataView => {
					// Use the same data source
					if (dataView.modelRef !== sourceId) {
						return;
					}

					let dataItems = dataView.dataItems;
					if (!dataItems) {
						return;
					}
					dataItems.forEach(dataItem => {
						let metadataColumn = this.visModelManager.getMetadataColumn(dataItem.itemId);
						if (metadataColumn && aModelItems.indexOf(metadataColumn.getId()) === -1) {
							aModelItems.push(metadataColumn.getId());
						}
					});
				});
			}
			return aModelItems;
		}
	});

	return VisQuerySupport;
});
